import { Download, FolderTree, PackageSearch, Share2, Sparkles, Target } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { type ContextBundleRequest, type ContextBundleResult, type ExportRequest } from "@graphmind/api-client";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/overlays";
import { Input } from "@/components/ui/primitives";
import { trackJob } from "@/hooks/use-upload";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useActivity } from "@/stores/activity";

type Mode = "description" | "markdown" | "graphiti" | "context";

const MODES: { id: Mode; label: string; icon: typeof Sparkles }[] = [
  { id: "description", label: "By description", icon: Sparkles },
  { id: "markdown", label: "Markdown structure", icon: FolderTree },
  { id: "graphiti", label: "Agent context", icon: Share2 },
  { id: "context", label: "Relevant context", icon: Target },
];

type ContextShape = ContextBundleRequest["shape"];

const SHAPES: { id: ContextShape; label: string; hint: string }[] = [
  { id: "markdown", label: "Markdown", hint: "Tiered agentic structure" },
  { id: "graphiti", label: "Graphiti", hint: "Agent-context JSON" },
  { id: "index", label: "Index", hint: "Summary map" },
];

const BUDGETS = [4_000, 8_000, 16_000, 32_000];

function fmtTokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k` : String(n);
}

/** Filesystem-safe slug for a downloaded bundle's filename. */
function slugify(text: string): string {
  const slug = text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  return slug || "bundle";
}

/** Export dialog with four modes:
 *  - by description: a model selects matching documents from their metadata;
 *  - markdown structure: the whole vault as a folder-shaped markdown tree;
 *  - agent context: the graph in Zep Graphiti's entities/edges/episodes format;
 *  - relevant context: the find -> bundle engine — documents relevant to a
 *    query, packaged token-budgeted in one shape, built and downloaded
 *    immediately (synchronous; not an export job). */
export function SmartExportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [mode, setMode] = useState<Mode>("description");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const [contextQuery, setContextQuery] = useState("");
  const [contextShape, setContextShape] = useState<ContextShape>("markdown");
  const [contextBudget, setContextBudget] = useState(8_000);
  const [contextBusy, setContextBusy] = useState(false);
  const [contextResult, setContextResult] = useState<ContextBundleResult | null>(null);

  useEffect(() => {
    if (!open) {
      setDescription("");
      setMode("description");
      setContextQuery("");
      setContextShape("markdown");
      setContextBudget(8_000);
      setContextResult(null);
    }
  }, [open]);

  const startExport = async (
    req: ExportRequest,
    { title, pending, ready }: { title: string; pending: string; ready: string },
  ) => {
    setBusy(true);
    try {
      const { job_id } = await api.createExport(req);
      useActivity.getState().upsert({ id: job_id, kind: "export", title, status: "queued" });
      onOpenChange(false);
      toast(pending);
      const job = await trackJob(job_id);
      if (job.status === "done") {
        toast.success(ready, {
          duration: 12_000,
          action: {
            label: "Download",
            onClick: () => window.open(`/api/exports/${job_id}/download`, "_blank"),
          },
        });
      } else {
        toast.error("Export failed", { description: job.error ?? "" });
      }
    } catch (err) {
      toast.error("Couldn't start the export", {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusy(false);
    }
  };

  const runDescription = () => {
    const desc = description.trim();
    if (!desc) return;
    void startExport(
      { kind: "smart_bundle", description: desc },
      {
        title: `Smart export · ${desc}`,
        pending: `Gathering “${desc}”…`,
        ready: "Smart export ready",
      },
    );
  };

  const runMarkdown = () =>
    void startExport(
      { kind: "markdown_structure" },
      {
        title: "Markdown structure export",
        pending: "Building your vault-shaped Markdown tree…",
        ready: "Markdown structure ready",
      },
    );

  const runGraphiti = () =>
    void startExport(
      { kind: "graphiti_bundle" },
      {
        title: "Agent context export",
        pending: "Serializing your graph as agent context…",
        ready: "Agent context bundle ready",
      },
    );

  // The find -> bundle engine is synchronous: it returns the packaged bundle
  // directly (no job id, no activity entry, no polling) — build it in place.
  const buildContext = async () => {
    const query = contextQuery.trim();
    if (!query) return;
    setContextBusy(true);
    setContextResult(null);
    try {
      const result = await api.contextBundle({
        query,
        shape: contextShape,
        token_budget: contextBudget,
        limit: 10,
      });
      if (result.report.returned === 0) {
        toast.error("No relevant documents found", {
          description: "Try a broader description of what you need.",
        });
        return;
      }
      setContextResult(result);
    } catch (err) {
      toast.error("Couldn't build the context bundle", {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setContextBusy(false);
    }
  };

  const downloadContext = () => {
    if (!contextResult) return;
    const { bundle } = contextResult;
    const slug = slugify(contextQuery);
    const isJson = bundle.shape === "graphiti";
    const body = isJson ? JSON.stringify(bundle.payload, null, 2) : (bundle.content ?? "");
    const blob = new Blob([body], { type: isJson ? "application/json" : "text/markdown" });
    const filename = `context-${slug}.${isJson ? "json" : "md"}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Export" className="w-[min(620px,92vw)]">
        <div className="space-y-4">
          {/* Mode pills: stacked full-width rows on phones (each an easy touch
              target with a single-line label), one row from sm: up. */}
          <div className="flex flex-col gap-1 rounded-lg border border-border bg-background p-1 sm:flex-row">
            {MODES.map((m) => {
              const Icon = m.icon;
              const active = mode === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMode(m.id)}
                  aria-pressed={active}
                  className={cn(
                    "flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs transition",
                    active
                      ? "bg-primary text-primary-foreground font-semibold shadow-glow"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent",
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {m.label}
                </button>
              );
            })}
          </div>

          {mode === "description" && (
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                runDescription();
              }}
            >
              <div className="flex items-start gap-3 rounded-lg border border-border bg-background p-3">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Describe the bundle you want and the model decides its scope from each
                  document&apos;s metadata — say <span className="text-foreground">“research papers”</span>{" "}
                  and you get research papers; <span className="text-foreground">“video game guides”</span>{" "}
                  and you get game guides. Matches are zipped with an index.
                </p>
              </div>
              <Input
                autoFocus
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="e.g. research papers, recipes, onboarding docs…"
                aria-label="What to gather"
              />
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] text-muted-foreground">
                  Bundles the matching documents&apos; markdown + originals into one zip
                </span>
                <Button type="submit" variant="default" disabled={!description.trim() || busy}>
                  <PackageSearch className="h-3.5 w-3.5" />
                  {busy ? "Gathering…" : "Gather & export"}
                </Button>
              </div>
            </form>
          )}

          {mode === "markdown" && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg border border-border bg-background p-3">
                <FolderTree className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Exports your whole library as a{" "}
                  <span className="text-foreground">folder-shaped Markdown tree</span> — every
                  document rendered to Markdown at its vault folder path, with{" "}
                  <span className="text-foreground">[[wikilinks]]</span> intact. Drop it into
                  Obsidian and your structure comes along.
                </p>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] text-muted-foreground">
                  Mirrors your vault layout · one .md per document
                </span>
                <Button type="button" variant="default" disabled={busy} onClick={runMarkdown}>
                  <FolderTree className="h-3.5 w-3.5" />
                  {busy ? "Exporting…" : "Export Markdown tree"}
                </Button>
              </div>
            </div>
          )}

          {mode === "graphiti" && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg border border-border bg-background p-3">
                <Share2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Serializes your graph in{" "}
                  <span className="text-foreground">Zep Graphiti&apos;s agent-context format</span> —
                  entities, relationship facts with timestamps, and source episodes as JSON,
                  plus a README that tells an agent how to consume it. Feed it straight to a
                  temporally-aware agent.
                </p>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] text-muted-foreground">
                  entities.json · edges.json · episodes.json · manifest · README
                </span>
                <Button type="button" variant="default" disabled={busy} onClick={runGraphiti}>
                  <Share2 className="h-3.5 w-3.5" />
                  {busy ? "Exporting…" : "Export agent context"}
                </Button>
              </div>
            </div>
          )}

          {mode === "context" && (
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                void buildContext();
              }}
            >
              <div className="flex items-start gap-3 rounded-lg border border-border bg-background p-3">
                <Target className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Finds exactly the documents relevant to your query and packages them,
                  token-budgeted, in the shape you pick. Built instantly — no export job, no
                  waiting.
                </p>
              </div>

              <Input
                autoFocus
                value={contextQuery}
                onChange={(event) => setContextQuery(event.target.value)}
                placeholder="Describe what you need — e.g. “everything on retrieval-augmented generation”"
                aria-label="What context to find"
              />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="mb-1.5 font-mono text-2xs uppercase tracking-wide text-muted-foreground">
                    Format
                  </p>
                  <div className="flex items-center rounded border border-border bg-background p-0.5">
                    {SHAPES.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setContextShape(s.id)}
                        aria-label={`Format ${s.label}`}
                        aria-pressed={contextShape === s.id}
                        title={s.hint}
                        className={cn(
                          "flex-1 rounded px-1.5 py-1 text-[11px] transition-colors",
                          contextShape === s.id
                            ? "bg-primary/20 text-primary"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-1.5 font-mono text-2xs uppercase tracking-wide text-muted-foreground">
                    Token budget
                  </p>
                  <div className="flex items-center rounded border border-border bg-background p-0.5">
                    {BUDGETS.map((b) => (
                      <button
                        key={b}
                        type="button"
                        onClick={() => setContextBudget(b)}
                        aria-label={`Token budget ${fmtTokens(b)}`}
                        aria-pressed={contextBudget === b}
                        className={cn(
                          "flex-1 rounded px-1.5 py-1 font-mono text-[11px] transition-colors",
                          contextBudget === b
                            ? "bg-primary/20 text-primary"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {fmtTokens(b)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {contextResult && (
                <div className="space-y-2 rounded-lg border border-border bg-background p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[12.5px] text-foreground">
                      <span className="font-semibold text-primary">
                        {contextResult.report.returned}
                      </span>{" "}
                      document{contextResult.report.returned === 1 ? "" : "s"} · ~
                      {fmtTokens(contextResult.bundle.token_estimate)} tokens
                    </p>
                    {contextResult.bundle.truncated && (
                      <span className="shrink-0 rounded border border-destructive/30 bg-destructive/15 px-1.5 py-px font-mono text-2xs text-destructive">
                        truncated
                      </span>
                    )}
                  </div>
                  <p className="font-mono text-2xs text-muted-foreground">
                    {contextResult.bundle.included.length} included ·{" "}
                    {contextResult.bundle.dropped.length} dropped
                  </p>
                  {contextResult.documents.length > 0 && (
                    <ul className="space-y-0.5">
                      {contextResult.documents.slice(0, 5).map((d) => (
                        <li
                          key={d.node_id}
                          className="truncate text-2xs text-muted-foreground"
                          title={d.label}
                        >
                          · {d.label}
                        </li>
                      ))}
                      {contextResult.documents.length > 5 && (
                        <li className="text-2xs text-muted-foreground">
                          +{contextResult.documents.length - 5} more
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] text-muted-foreground">
                  Synchronous — builds in place, nothing to track
                </span>
                <div className="flex items-center gap-2">
                  {contextResult && (
                    <Button type="button" variant="outline" onClick={downloadContext}>
                      <Download className="h-3.5 w-3.5" />
                      Download
                    </Button>
                  )}
                  <Button
                    type="submit"
                    variant="default"
                    disabled={!contextQuery.trim() || contextBusy}
                  >
                    <Target className="h-3.5 w-3.5" />
                    {contextBusy ? "Building…" : "Build"}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
